// Composer tray for Dev Console attachments: a one-line summary for big
// packages (folders, thousands of files), thumbnails for a few screenshots,
// an expandable path list, remove-by-folder, and the upload progress bar.
import React, { useMemo, useState } from 'react';
import { summarize, fmtBytes, isImage } from './attachments';

const THUMB_MAX = 8;    // show thumbnails/chips up to this many files
const LIST_MAX = 300;   // paths listed when expanded

// `onPin(i)` toggles "keep for this project" on a loose file: once sent it
// is pinned as a project reference and rides every later turn on the repo
// (see ReferencesStrip in DevConsole.js).
export default function AttachmentTray({ entries, onRemove, onRemoveFolder, onClear, onPin, upload }) {
  const [open, setOpen] = useState(false);
  const sum = useMemo(() => summarize(entries), [entries]);
  const thumbs = useMemo(() => entries.slice(0, THUMB_MAX).map((e) => ({ ...e, url: isImage(e.file) ? URL.createObjectURL(e.file) : null })), [entries]);
  if (!entries.length && !upload) return null;

  if (upload) {
    const pct = upload.bytesTotal ? Math.round((upload.bytesDone / upload.bytesTotal) * 100) : Math.round((upload.done / Math.max(1, upload.total)) * 100);
    return (
      <div className="devc-tray devc-tray-upload" role="status" aria-live="polite">
        <div className="devc-tray-row">
          <strong>Uploading {upload.done}/{upload.total}</strong>
          <span className="devc-dim">{fmtBytes(upload.bytesDone)} / {fmtBytes(upload.bytesTotal)}{upload.current ? ` · ${upload.current}` : ''}</span>
          <span style={{ flex: 1 }} />
          {upload.onCancel && <button type="button" className="devc-filter" onClick={upload.onCancel}>Cancel</button>}
        </div>
        <div className="devc-tray-bar"><div style={{ width: `${pct}%` }} /></div>
      </div>
    );
  }

  const compact = entries.length <= THUMB_MAX && !sum.folders.length;
  return (
    <div className="devc-tray">
      {compact ? (
        <div className="devc-files">
          {thumbs.map((e, i) => (
            <span key={e.relPath + e.file.size} className={`devc-filechip${e.pin ? ' is-pinned' : ''}`} title={e.pin ? `${e.relPath} — kept for this project after sending` : e.relPath}>
              {e.url ? <img src={e.url} alt="" /> : <span aria-hidden="true">📄 </span>}
              {e.file.name}
              {onPin && (
                <button
                  type="button"
                  className="devc-filechip-pin"
                  aria-pressed={!!e.pin}
                  aria-label={e.pin ? `Stop keeping ${e.file.name} for this project` : `Keep ${e.file.name} for this project`}
                  title={e.pin ? 'Kept for this project: every later session gets this file' : 'Keep for this project — hand this file to every later session'}
                  onClick={() => onPin(i)}
                >📌</button>
              )}
              <button type="button" onClick={() => onRemove(i)} aria-label={`Remove ${e.file.name}`}>×</button>
            </span>
          ))}
        </div>
      ) : (
        <>
          <div className="devc-tray-row">
            <span aria-hidden="true">🗂</span>
            <strong>
              {sum.count.toLocaleString()} file{sum.count === 1 ? '' : 's'} · {fmtBytes(sum.bytes)}
              {sum.folders.length ? ` · ${sum.folders.length} folder${sum.folders.length === 1 ? '' : 's'}` : ''}
            </strong>
            <span className="devc-dim">
              {[sum.images && `${sum.images} image${sum.images === 1 ? '' : 's'}`, sum.docs && `${sum.docs} doc${sum.docs === 1 ? '' : 's'}`, sum.other && `${sum.other} other`].filter(Boolean).join(' · ')}
            </span>
            <span style={{ flex: 1 }} />
            <button type="button" className="devc-filter" onClick={() => setOpen(!open)}>{open ? 'Hide files' : 'Show files'}</button>
            <button type="button" className="devc-filter" onClick={onClear}>Remove all</button>
          </div>
          {sum.folders.length > 0 && (
            <div className="devc-tray-folders">
              {sum.folders.map((f) => (
                <span key={f} className="devc-filechip" title={`Folder ${f}`}>
                  📁 {f}/
                  <button type="button" onClick={() => onRemoveFolder(f)} aria-label={`Remove folder ${f}`}>×</button>
                </span>
              ))}
              {thumbs.filter((e) => !e.relPath.includes('/')).map((e) => (
                <span key={e.relPath + e.file.size} className="devc-filechip" title={e.relPath}>
                  {e.url ? <img src={e.url} alt="" /> : <span aria-hidden="true">📄 </span>}{e.file.name}
                </span>
              ))}
            </div>
          )}
          {open && (
            <ul className="devc-tray-list">
              {entries.slice(0, LIST_MAX).map((e) => (
                <li key={e.relPath + e.file.size}><code>{e.relPath}</code> <span className="devc-dim">{fmtBytes(e.file.size)}</span></li>
              ))}
              {entries.length > LIST_MAX && <li className="devc-dim">… and {(entries.length - LIST_MAX).toLocaleString()} more</li>}
            </ul>
          )}
          <div className="devc-dim devc-tray-hint">
            The agent gets the folder tree and the key documents inline; the whole package is staged in its workspace
            {sum.images > 0 ? ` (the first ${Math.min(sum.images, 20)} images are shown to the model directly)` : ''}.
          </div>
        </>
      )}
    </div>
  );
}

// ```questions from the agent → a small form. onAnswer(answers) sends them
// back as the next turn; disabled once answered (the user event records it).
export function QuestionsCard({ e, answered, onAnswer, busy }) {
  const [values, setValues] = useState(() => Object.fromEntries((e.questions || []).map((q) => [q.id, q.allowMultiple ? [] : (q.recommended || '')])));
  const [other, setOther] = useState({});
  const qs = e.questions || [];
  const set = (id, v) => setValues((prev) => ({ ...prev, [id]: v }));
  const toggle = (id, opt) => setValues((prev) => {
    const cur = Array.isArray(prev[id]) ? prev[id] : [];
    return { ...prev, [id]: cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt] };
  });
  const submit = () => {
    const out = {};
    for (const q of qs) {
      const v = values[q.id];
      const o = (other[q.id] || '').trim();
      if (q.allowMultiple) out[q.id] = [...(Array.isArray(v) ? v : []), ...(o ? [o] : [])];
      else out[q.id] = o || v || '';
    }
    onAnswer(out);
  };
  return (
    <div className={`devc-questions${answered ? ' is-answered' : ''}`}>
      <div className="devc-msg-who">Agent needs your input{answered ? ' · answered' : ''}</div>
      {qs.map((q, i) => (
        <div key={q.id} className="devc-q">
          <div className="devc-q-text">{i + 1}. {q.question}</div>
          <div className="devc-q-opts">
            {q.options.map((opt) => (
              <label key={opt} className={`devc-q-opt${(q.allowMultiple ? (values[q.id] || []).includes(opt) : values[q.id] === opt) ? ' active' : ''}`}>
                <input
                  type={q.allowMultiple ? 'checkbox' : 'radio'}
                  name={`${e.id}-${q.id}`}
                  disabled={answered}
                  checked={q.allowMultiple ? (values[q.id] || []).includes(opt) : values[q.id] === opt}
                  onChange={() => (q.allowMultiple ? toggle(q.id, opt) : set(q.id, opt))}
                />
                {opt}{q.recommended === opt && <span className="devc-chip blue" style={{ marginLeft: 6 }}>recommended</span>}
              </label>
            ))}
            {!answered && (
              <input
                className="devc-q-other"
                placeholder={q.options.length ? 'Other…' : 'Your answer…'}
                value={other[q.id] || ''}
                onChange={(ev) => setOther((prev) => ({ ...prev, [q.id]: ev.target.value }))}
              />
            )}
          </div>
        </div>
      ))}
      {!answered && (
        <div className="devc-approve-actions" data-agent-guard>
          <button type="button" className="devc-btn primary" disabled={busy} onClick={submit}>Send answers</button>
          <span className="devc-dim">Unanswered questions use the agent&apos;s recommendation.</span>
        </div>
      )}
    </div>
  );
}
